import { useState, useEffect } from 'react';
import { X, Upload, Calendar, Tag, Save, Eye, Clock } from 'lucide-react';
import { adminThoughtsApi, adminCategoriesApi } from '../utils/apiService';
import { toast } from 'sonner';

interface CreateThoughtProps {
  onClose: () => void;
  onSave: (thought: ThoughtFormData) => void;
  editData?: ThoughtFormData;
}

export interface ThoughtFormData {
  id?: string;
  text: string;
  author: string;
  category: string;
  imageUrl: string;
  status: 'draft' | 'scheduled' | 'published';
  scheduledDate?: string;
  scheduledTime?: string;
  tags?: string[];
}

export default function CreateThought({ onClose, onSave, editData }: CreateThoughtProps) {
  const [formData, setFormData] = useState<ThoughtFormData>(editData || {
    text: '',
    author: '',
    category: '',
    imageUrl: '',
    status: 'draft',
    tags: [],
  });
  const [tagInput, setTagInput] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageInputMode, setImageInputMode] = useState<'upload' | 'url'>(
    editData?.imageUrl ? 'url' : 'upload'
  );
  const [imageUrlInput, setImageUrlInput] = useState(editData?.imageUrl || '');

  const [showPreview, setShowPreview] = useState(false);
  const [imagePreview, setImagePreview] = useState(formData.imageUrl || '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categories, setCategories] = useState<Array<{ _id: string; name: string; color: string; order?: number }>>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      setIsLoadingCategories(true);
      try {
        const response = await adminCategoriesApi.getAll();
        if (response && response.categories) {
          // Sort categories by order field (ascending)
          const sortedCategories = response.categories
            .filter(cat => cat.name) // Ensure category has a name
            .sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
            .map(cat => ({
              _id: cat._id,
              name: cat.name,
              color: cat.color || '#2B7A7E',
              order: cat.order
            }));
          setCategories(sortedCategories);
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
        toast.error('Failed to load categories');
        // Fallback to static categories
        setCategories([
          { _id: '1', name: 'Motivation', color: '#2B7A7E' },
          { _id: '2', name: 'Life', color: '#2B7A7E' },
          { _id: '3', name: 'Self', color: '#2B7A7E' },
          { _id: '4', name: 'Success', color: '#2B7A7E' },
          { _id: '5', name: 'Philosophy', color: '#2B7A7E' },
          { _id: '6', name: 'Love', color: '#2B7A7E' },
          { _id: '7', name: 'Wisdom', color: '#2B7A7E' },
          { _id: '8', name: 'Happiness', color: '#2B7A7E' },
          { _id: '9', name: 'Growth', color: '#2B7A7E' },
          { _id: '10', name: 'Mindfulness', color: '#2B7A7E' },
        ]);
      } finally {
        setIsLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  const handleInputChange = (field: keyof ThoughtFormData, value: string | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.text.trim()) {
      newErrors.text = 'Thought text is required';
    } else if (formData.text.length < 10) {
      newErrors.text = 'Thought must be at least 10 characters';
    } else if (formData.text.length > 500) {
      newErrors.text = 'Thought must be less than 500 characters';
    }

    if (!formData.author.trim()) {
      newErrors.author = 'Author name is required';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    // Image is now optional for both create and edit
    // Removed the image requirement validation

    if (formData.status === 'scheduled') {
      if (!formData.scheduledDate) {
        newErrors.scheduledDate = 'Scheduled date is required';
      }
      if (!formData.scheduledTime) {
        newErrors.scheduledTime = 'Scheduled time is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Compute scheduled date ISO string once
      let scheduledDateISO: string | undefined;
      if (formData.status === 'scheduled' && formData.scheduledDate && formData.scheduledTime) {
        scheduledDateISO = new Date(`${formData.scheduledDate}T${formData.scheduledTime}:00`).toISOString();
      }

      if (formData.id) {
        // UPDATE — always send FormData (server PUT route uses multer)
        const apiFormData = new FormData();
        apiFormData.append('text', formData.text);
        apiFormData.append('author', formData.author);
        apiFormData.append('category', formData.category);
        apiFormData.append('status', formData.status);
        if (scheduledDateISO) apiFormData.append('scheduledDate', scheduledDateISO);
        if (imageFile) {
          apiFormData.append('image', imageFile);
        } else if (formData.imageUrl) {
          apiFormData.append('imageUrl', formData.imageUrl);
        }
        apiFormData.append('tags', JSON.stringify(formData.tags || []));
        await adminThoughtsApi.update(formData.id, apiFormData);
      } else {
        // CREATE — always send FormData (server uses multer)
        const apiFormData = new FormData();
        apiFormData.append('text', formData.text);
        apiFormData.append('author', formData.author);
        apiFormData.append('category', formData.category);
        apiFormData.append('status', formData.status);
        if (scheduledDateISO) apiFormData.append('scheduledDate', scheduledDateISO);
        if (imageFile) {
          apiFormData.append('image', imageFile);
        } else if (formData.imageUrl) {
          apiFormData.append('imageUrl', formData.imageUrl);
        }
        apiFormData.append('tags', JSON.stringify(formData.tags || []));
        await adminThoughtsApi.create(apiFormData);
      }

      toast.success(formData.id ? 'Thought updated successfully!' : 'Thought created successfully!');
      onSave(formData);
      onClose();
    } catch (error: any) {
      console.error('Failed to save thought:', error);
      toast.error(error.message || 'Failed to save thought. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveAsDraft = async () => {
    setFormData(prev => ({ ...prev, status: 'draft' }));
    if (formData.text && formData.author && formData.category) {
      onSave({ ...formData, status: 'draft' });
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#2B7A7E] to-[#1e5a5d] text-white px-8 py-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">
              {editData ? 'Edit Thought' : 'Create New Thought'}
            </h2>
            <p className="text-white/80 text-sm mt-1">
              Share wisdom and inspiration with your community
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors flex items-center gap-2"
            >
              <Eye className="w-4 h-4" />
              {showPreview ? 'Edit' : 'Preview'}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8">
          {showPreview ? (
            <ThoughtPreview formData={formData} imagePreview={imagePreview} />
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Thought Text */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Thought / Quote *
                </label>
                <textarea
                  value={formData.text}
                  onChange={(e) => handleInputChange('text', e.target.value)}
                  placeholder="Enter an inspiring thought or quote..."
                  className={`w-full px-4 py-3 border-2 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-[#2B7A7E] transition-all ${
                    errors.text ? 'border-red-300' : 'border-slate-200'
                  }`}
                  rows={4}
                />
                <div className="flex items-center justify-between mt-2">
                  {errors.text && (
                    <p className="text-sm text-red-600">{errors.text}</p>
                  )}
                  <p className={`text-sm ml-auto ${
                    formData.text.length > 500 ? 'text-red-600' : 'text-slate-400'
                  }`}>
                    {formData.text.length}/500
                  </p>
                </div>
              </div>

              {/* Author */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Author *
                </label>
                <input
                  type="text"
                  value={formData.author}
                  onChange={(e) => handleInputChange('author', e.target.value)}
                  placeholder="e.g., Maya Angelou, Steve Jobs, Anonymous..."
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2B7A7E] transition-all ${
                    errors.author ? 'border-red-300' : 'border-slate-200'
                  }`}
                />
                {errors.author && (
                  <p className="text-sm text-red-600 mt-2">{errors.author}</p>
                )}
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Category *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  disabled={isLoadingCategories}
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2B7A7E] transition-all ${
                    errors.category ? 'border-red-300' : 'border-slate-200'
                  } ${isLoadingCategories ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <option value="">
                    {isLoadingCategories ? 'Loading categories...' : 'Select a category...'}
                  </option>
                  {categories.map((cat) => (
                    <option key={cat._id} value={cat.name}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                {errors.category && (
                  <p className="text-sm text-red-600 mt-2">{errors.category}</p>
                )}
                {categories.length > 0 && (
                  <p className="text-xs text-slate-500 mt-2">
                    💡 Tip: {categories.length} categor{categories.length === 1 ? 'y' : 'ies'} available. Manage categories in the Categories tab.
                  </p>
                )}
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  <span className="flex items-center gap-2"><Tag className="w-4 h-4" /> Tags (Optional)</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
                        e.preventDefault();
                        const newTag = tagInput.trim().replace(/,$/, '');
                        if (newTag && !(formData.tags || []).includes(newTag)) {
                          handleInputChange('tags', [...(formData.tags || []), newTag]);
                        }
                        setTagInput('');
                      }
                    }}
                    placeholder="Type a tag and press Enter"
                    className="flex-1 px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2B7A7E] transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const newTag = tagInput.trim();
                      if (newTag && !(formData.tags || []).includes(newTag)) {
                        handleInputChange('tags', [...(formData.tags || []), newTag]);
                      }
                      setTagInput('');
                    }}
                    className="px-4 py-3 bg-[#2B7A7E] text-white rounded-xl hover:bg-[#236266] transition-colors"
                  >
                    Add
                  </button>
                </div>
                {(formData.tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {(formData.tags || []).map((tag, i) => (
                      <span key={i} className="flex items-center gap-1 px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm">
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleInputChange('tags', (formData.tags || []).filter((_, idx) => idx !== i))}
                          className="ml-1 text-slate-400 hover:text-red-500 transition-colors"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Background Image */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Background Image (Optional)
                </label>

                {/* Toggle */}
                <div className="flex gap-1 p-1 bg-slate-100 rounded-lg mb-3 w-fit">
                  <button
                    type="button"
                    onClick={() => {
                      setImageInputMode('upload');
                      setImageUrlInput('');
                      setImagePreview(imageFile ? URL.createObjectURL(imageFile) : '');
                      handleInputChange('imageUrl', '');
                    }}
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                      imageInputMode === 'upload'
                        ? 'bg-white text-[#2B7A7E] shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    Upload File
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setImageInputMode('url');
                      setImageFile(null);
                      setImagePreview(formData.imageUrl || '');
                      setImageUrlInput(formData.imageUrl || '');
                    }}
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                      imageInputMode === 'url'
                        ? 'bg-white text-[#2B7A7E] shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    Image URL
                  </button>
                </div>

                {imageInputMode === 'url' ? (
                  <div className="space-y-3">
                    <input
                      type="url"
                      value={imageUrlInput}
                      onChange={(e) => {
                        setImageUrlInput(e.target.value);
                        handleInputChange('imageUrl', e.target.value);
                        setImagePreview(e.target.value);
                      }}
                      placeholder="https://example.com/image.jpg"
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2B7A7E] transition-all"
                    />
                    {imagePreview && (
                      <div className="relative rounded-xl overflow-hidden border-2 border-slate-200">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="w-full h-64 object-contain bg-slate-50"
                          onError={() => setImagePreview('')}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setImageUrlInput('');
                            setImagePreview('');
                            handleInputChange('imageUrl', '');
                          }}
                          className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                ) : imagePreview ? (
                  <div className="relative rounded-xl overflow-hidden border-2 border-slate-200">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-64 object-contain bg-slate-50"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setImagePreview('');
                        setImageFile(null);
                        handleInputChange('imageUrl', '');
                      }}
                      className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/50 text-white text-xs rounded-lg">
                      {imageFile ? imageFile.name : 'Current image'}
                    </div>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-[#2B7A7E] hover:bg-[#2B7A7E]/5 transition-all">
                    <Upload className="w-8 h-8 text-slate-400 mb-2" />
                    <span className="text-sm font-medium text-slate-600">Click to upload image</span>
                    <span className="text-xs text-slate-400 mt-1">PNG, JPG, JPEG, WEBP up to 5MB</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          if (file.size > 5 * 1024 * 1024) {
                            toast.error('Image must be less than 5MB');
                            return;
                          }
                          setImageFile(file);
                          setImagePreview(URL.createObjectURL(file));
                          handleInputChange('imageUrl', '');
                        }
                      }}
                    />
                  </label>
                )}
              </div>

              {/* Publishing Options */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-3">
                  Publishing Options
                </label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => handleInputChange('status', 'draft')}
                    className={`p-4 border-2 rounded-xl transition-all ${
                      formData.status === 'draft'
                        ? 'border-[#2B7A7E] bg-[#2B7A7E]/5'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <Save className={`w-5 h-5 ${
                        formData.status === 'draft' ? 'text-[#2B7A7E]' : 'text-slate-400'
                      }`} />
                      <span className="text-sm font-semibold">Draft</span>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleInputChange('status', 'scheduled')}
                    className={`p-4 border-2 rounded-xl transition-all ${
                      formData.status === 'scheduled'
                        ? 'border-[#2B7A7E] bg-[#2B7A7E]/5'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <Clock className={`w-5 h-5 ${
                        formData.status === 'scheduled' ? 'text-[#2B7A7E]' : 'text-slate-400'
                      }`} />
                      <span className="text-sm font-semibold">Schedule</span>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleInputChange('status', 'published')}
                    className={`p-4 border-2 rounded-xl transition-all ${
                      formData.status === 'published'
                        ? 'border-[#2B7A7E] bg-[#2B7A7E]/5'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <Upload className={`w-5 h-5 ${
                        formData.status === 'published' ? 'text-[#2B7A7E]' : 'text-slate-400'
                      }`} />
                      <span className="text-sm font-semibold">Publish Now</span>
                    </div>
                  </button>
                </div>
              </div>

              {/* Schedule Date/Time (shown when scheduled) */}
              {formData.status === 'scheduled' && (
                <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-xl">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    <span className="font-semibold text-blue-900">Schedule Publication</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-blue-900 mb-2">
                        Date
                      </label>
                      <input
                        type="date"
                        value={formData.scheduledDate || ''}
                        onChange={(e) => handleInputChange('scheduledDate', e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.scheduledDate ? 'border-red-300' : 'border-blue-200'
                        }`}
                      />
                      {errors.scheduledDate && (
                        <p className="text-sm text-red-600 mt-1">{errors.scheduledDate}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-blue-900 mb-2">
                        Time
                      </label>
                      <input
                        type="time"
                        value={formData.scheduledTime || ''}
                        onChange={(e) => handleInputChange('scheduledTime', e.target.value)}
                        className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.scheduledTime ? 'border-red-300' : 'border-blue-200'
                        }`}
                      />
                      {errors.scheduledTime && (
                        <p className="text-sm text-red-600 mt-1">{errors.scheduledTime}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-8 py-6 bg-slate-50">
          <div className="flex items-center justify-between gap-4">
            <button
              type="button"
              onClick={handleSaveAsDraft}
              className="px-6 py-3 text-slate-600 hover:bg-slate-200 rounded-lg transition-colors font-medium"
            >
              Save as Draft
            </button>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 border-2 border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-8 py-3 bg-gradient-to-r from-[#2B7A7E] to-[#1e5a5d] text-white rounded-lg hover:shadow-lg transition-all font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : formData.status === 'published' ? (
                  <>
                    <Upload className="w-5 h-5" />
                    Publish Now
                  </>
                ) : formData.status === 'scheduled' ? (
                  <>
                    <Clock className="w-5 h-5" />
                    Schedule Thought
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    Save Draft
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Preview Component
function ThoughtPreview({ formData, imagePreview }: { formData: ThoughtFormData; imagePreview: string }) {
  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-slate-200">
        {/* Image */}
        {imagePreview && (
          <div className="relative">
            <img
              src={imagePreview}
              alt="Background"
              className="w-full h-64 object-contain bg-slate-50"
            />
          </div>
        )}

        {/* Content */}
        <div className="p-8">
          {/* Quote */}
          <p className="text-slate-700 text-xl leading-relaxed mb-3 italic">
            {formData.text ? `"${formData.text}"` : 'Your thought will appear here...'}
          </p>

          {/* Author */}
          <p className="text-slate-600 mb-6">
            {formData.author ? `— ${formData.author}` : '— Author Name'}
          </p>

          {/* Category */}
          {formData.category && (
            <div className="mb-6">
              <span className="inline-block px-4 py-2 bg-[#2B7A7E]/10 text-[#2B7A7E] rounded-full text-sm font-semibold border-2 border-[#2B7A7E]/20">
                📋 {formData.category}
              </span>
            </div>
          )}

          {/* Status Badge */}
          <div className="flex items-center gap-3 pt-4 border-t border-slate-200">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
              formData.status === 'published' ? 'bg-green-100 text-green-700' :
              formData.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
              'bg-gray-100 text-gray-700'
            }`}>
              {formData.status.toUpperCase()}
            </span>
            {formData.status === 'scheduled' && formData.scheduledDate && (
              <span className="text-sm text-slate-600">
                Scheduled for {formData.scheduledDate} {formData.scheduledTime && `at ${formData.scheduledTime}`}
              </span>
            )}
          </div>

          {/* Mock Actions */}
          <div className="flex items-center gap-6 mt-6 text-sm text-slate-500">
            <span>❤️ 0</span>
            <span>💬 0</span>
            <span>🔗 0</span>
          </div>
        </div>
      </div>

      <div className="mt-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl">
        <p className="text-sm text-blue-900">
          <strong>Preview Mode:</strong> This is how your thought will appear to users. Click "Edit" to make changes.
        </p>
      </div>
    </div>
  );
}